// The movie projection's vocabulary, in one place. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SIX NAMED TIERS, AND THE ONLY NUMBER ANYBODY DERIVES IS 0–5
// ═══════════════════════════════════════════════════════════════════════════
//
// Measured rather than chosen — the handoff closed this question by reading the
// cog. `cogs/movie/cog.py` carries
//
//     POLL_SCORE_MAP      {'Godtier': 5, 'Niceee': 4, 'Good': 3,
//                          'Mid': 2, 'Bad': 1, 'Nahhh': 0}
//     RATING_POLL_OPTIONS [('Godtier','🔥'), ('Niceee','👌'), ('Good','👍'),
//                          ('Mid','🤨'), ('Bad','👎'), ('Nahhh','🤮')]
//
// and the tally the bot stores is keyed by the poll ANSWER TEXT, which is the
// bare label — it has to be, or `POLL_SCORE_MAP.get(label, 0)` would score every
// vote zero. So the strings below are the strings on the wire.
//
// ⚠️ THE `/10` IN THE COG IS SOMEBODY ELSE'S NUMBER. It is TMDb's
// `vote_average` — the film's public score, not the room's — and nothing here
// may render it. The blob publishes `scale_max` (5) precisely so a page does not
// have to guess, and `utils/projection_publisher.py` pins that constant against
// POLL_SCORE_MAP with a test rather than copying it and hoping.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THERE IS NO SUCH THING AS "WHAT YOU RATED"
// ═══════════════════════════════════════════════════════════════════════════
//
// Discord's poll hands the bot vote COUNTS per option and nothing else, so
// per-user votes are never persisted anywhere in this repo. The publisher
// deliberately does not ship `ratings_given` at all — *"an empty list on the
// wire would read to the next person as 'we have this and it is empty' rather
// than 'this does not exist', and a portal module would get designed against
// it."*
//
// The consequences, both applied below: a verdict renders as a TIER CHIP and
// never as a number attributed to a person, and there is no RATED stat.
//
// ⚠️ AN UNRECOGNISED LABEL IS RENDERED, NOT DROPPED, AND NEVER SCORED. If the
// poll's options ever change, the old tallies stay in the store forever. A page
// that dropped an unknown label would quietly under-report a night's votes; one
// that invented an emoji for it would be making something up. It renders as its
// own bare text, after the known tiers, and it cannot win a verdict it cannot
// be ranked against.

/** The six, in the order the poll shows them — which is score order. */
export const TIERS = Object.freeze([
  Object.freeze({ label: 'Godtier', emoji: '🔥', score: 5 }),
  Object.freeze({ label: 'Niceee', emoji: '👌', score: 4 }),
  Object.freeze({ label: 'Good', emoji: '👍', score: 3 }),
  Object.freeze({ label: 'Mid', emoji: '🤨', score: 2 }),
  Object.freeze({ label: 'Bad', emoji: '👎', score: 1 }),
  Object.freeze({ label: 'Nahhh', emoji: '🤮', score: 0 }),
])

const BY_LABEL = new Map(TIERS.map((tier) => [tier.label, tier]))

/** What we know about one label off the wire. `known: false` is a real answer. */
export function tierFor(label) {
  const known = BY_LABEL.get(label)
  if (known) return { ...known, known: true }
  return { label: String(label), emoji: null, score: null, known: false }
}

/**
 * How a tier is written on the page: emoji then name, or just the name.
 *
 * 🔴 THE EMOJI ARE THE PRODUCT'S OWN VOCABULARY, NOT DECORATION — they are the
 * buttons people press in the poll, so a member recognises the row before
 * reading it. They are NOT aria-hidden for that reason: "🔥 Godtier" read aloud
 * is what the thing is called.
 */
export function tierText(label) {
  const tier = tierFor(label)
  return tier.emoji === null ? tier.label : `${tier.emoji} ${tier.label}`
}

/**
 * A night's breakdown as chips, highest tier first.
 *
 * ⚠️ AN EMPTY BREAKDOWN IS A REAL STATE AND RETURNS `[]`. A night added by hand
 * carries `'ratings': {}` with a genuine `average_score` and `total_votes` —
 * measured in `cogs/movie/cog.py`, two separate manual-entry paths — so a
 * verdict can have an average and no chips at all. The panel renders the
 * average and claims no winning tier, which is P6-156's rule one level up: no
 * source, no claim.
 */
export function breakdownChips(breakdown) {
  if (!breakdown || typeof breakdown !== 'object' || Array.isArray(breakdown)) return []
  const chips = []
  for (const [label, raw] of Object.entries(breakdown)) {
    const count = Number(raw)
    if (!Number.isFinite(count) || count <= 0) continue
    chips.push({ ...tierFor(label), count, text: `${tierText(label)} ×${count}` })
  }
  // Known tiers by score, best first; anything unrecognised after them, by name,
  // so the order is stable rather than whatever key order the JSON arrived in.
  chips.sort((a, b) => {
    if (a.known !== b.known) return a.known ? -1 : 1
    if (a.known) return b.score - a.score
    return a.label.localeCompare(b.label)
  })
  return chips
}

/**
 * The tier the room actually landed on, or `null` when nothing can say.
 *
 * Most votes wins; a tie goes to the higher tier, which is the reading a room
 * would give it out loud. An unrecognised label can never win — it has no score
 * to be compared with, and naming it as the verdict would be asserting a
 * ranking we do not have.
 *
 * ⚠️ THE TIE-BREAK IS AN EQUIVALENT MUTANT TODAY, AND IT IS RECORDED AS ONE
 * RATHER THAN DELETED. `breakdownChips` already returns known tiers highest
 * first, so the first maximum this loop meets is always the highest-scoring one
 * and `&& chip.score > best.score` can never change the answer — a mutation
 * removing it survives, measured.
 *
 * It stays for the reason `guard.js` keeps `GUARDED_CANONICAL` and
 * `projections.js` keeps its `Array.isArray` pair: the sort in the other
 * function is one edit away from being the only thing holding this up, and the
 * honest reading of a belt-and-braces pair is to say which half is the belt.
 * Here the SORT is the belt; this line is the braces, and it is the half that
 * states the rule where somebody reading `winningTier` will find it.
 */
export function winningTier(breakdown) {
  const ranked = breakdownChips(breakdown).filter((chip) => chip.known)
  if (ranked.length === 0) return null
  let best = ranked[0]
  for (const chip of ranked) {
    if (chip.count > best.count || (chip.count === best.count && chip.score > best.score)) {
      best = chip
    }
  }
  return best
}

/**
 * A 0–5 average as a star bar.
 *
 * 🔴 `scaleMax` COMES FROM THE BLOB, never from here. The publisher ships it so
 * a page can render "3.8 / 5" without guessing, and a hardcoded 5 in this file
 * would be exactly the copied constant that module refused to leave lying
 * around. Returns `null` when there is no average — a bar of five empty stars
 * is a claim that the room hated everything.
 */
export function starBar(average, scaleMax) {
  const max = Number(scaleMax)
  const value = Number(average)
  if (!Number.isFinite(max) || max <= 0) return null
  if (average === null || average === undefined || !Number.isFinite(value)) return null
  const filled = Math.max(0, Math.min(max, Math.round(value)))
  return {
    fill: '★'.repeat(filled),
    empty: '☆'.repeat(Math.max(0, max - filled)),
    // The bar is decorative; this sentence is the accessible one, and it carries
    // the scale because "3.4" alone is meaningless and "3.4 / 5" is not.
    text: `${value.toFixed(1)} of ${max}`,
  }
}
